import { z } from 'zod';

/**
 * What a stored object **is** — and therefore who may read it and how long it
 * is kept (N12).
 *
 * The kind is the axis every media policy hangs off, so it is a closed set
 * rather than a free string: `id_document` and `selfie` are verification
 * evidence readable by their owner and gender-matched reviewers alone, while
 * `profile_photo` is member-facing content subject to the visibility rules.
 * A kind whose consumer does not exist yet is not declared here.
 *
 * `brand_asset` is the first **platform-owned** kind: it belongs to the
 * deployment rather than to a member, is uploaded by staff through
 * `admin/media`, and is what a `media`-controlled setting such as
 * `brand.logoMediaId` points at. `payment_evidence` is the second, and it is
 * platform-owned for a sharper reason than the logo's: the receipt behind a
 * direct payment is the deployment's financial record, so the member whose
 * order is in dispute is not its owner and cannot delete it. Both ownership
 * models are declared in the kind policy registry, not inferred from a name.
 *
 * `venue_photo` is the third platform-owned kind and the first of any kind that
 * an ordinary member may read **without** an owner's policy being consulted: a
 * partner institution's meeting place is part of what the product shows its
 * members, and it belongs to the deployment rather than to anybody. That
 * combination is what `MediaKindPolicy.memberRead` exists to say — see
 * `member-read.ts` for why a boolean could not.
 *
 * `payment_receipt` (F3) is the member's own upload of a transfer slip, bound
 * to the direct-payment order the member opens from the phone. It is
 * **member-owned** — the member uploaded it through the ordinary member presign
 * and finalize, exactly as an `id_document` — and that is what keeps the
 * ordinary upload path honest: a platform kind's presign is gated by its manage
 * permission, and a member holds none. What `payment_evidence`'s ownership
 * argument protected is protected here by a second axis instead:
 * `ownerDeletable: false`, so the receipt is the member's to send and read but
 * not to destroy while it stands as the record of a payment.
 */
export const MEDIA_KINDS = [
  'id_document',
  'selfie',
  'profile_photo',
  'brand_asset',
  'payment_evidence',
  'venue_photo',
  'payment_receipt',
] as const;

export const MediaKindSchema = z.enum(MEDIA_KINDS);

export type MediaKind = (typeof MEDIA_KINDS)[number];

const KIND_SET: ReadonlySet<string> = new Set<string>(MEDIA_KINDS);

/**
 * Narrows a plain string — what a query parameter passes through before the api
 * can treat it as a kind. Fail-closed: an unknown kind has no policy, and a
 * lookup against `undefined` would be an upload with no constraints at all.
 */
export function isMediaKind(value: string): value is MediaKind {
  return KIND_SET.has(value);
}
